/**
 * The server words its roster refusals in English; each is matched here so an mn or ru reader gets it in their language, with its names and values as sent.
 * ⛔ Call at render only: `offersOverwrite`, `hasRefusal`, `massClearCount` and `classifyClearRefusal` read the English, so keep the raw message in state.
 */
import { errorMessage } from '../api'

type Translate = (key: string, options?: Record<string, unknown>) => string
type Rule = readonly [pattern: RegExp, key: string, params?: readonly string[]]

const WIRE_PREFIX =
  /^(?:Value error, |(?:cell_invalid|cell_conflict|outside_horizon|unknown_code|leave_range|plan_read_only|plan_mass_clear|published_view|roster_access|city_range): )/

const ON_DAY = /^(.+?) (\d{4}-\d{2}-\d{2}): (.+)$/
const DAY = '(\\d{4}-\\d{2}-\\d{2})'

const RULES: readonly Rule[] = [
  [/^rowLayout must be an object with `order` and `hidden` lists of roster names$/, 'layoutShape'],
  [/^rowLayout\.(\w+) must be a list of roster names$/, 'layoutList', ['field']],
  [/^rowLayout\.(\w+) lists (\d+) names; at most (\d+)$/, 'layoutTooMany', ['field', 'n', 'max']],
  [/^rowLayout\.(\w+) holds a name longer than (\d+) characters$/, 'layoutNameLong', ['field', 'max']],

  [/^(.+): (\w+) is longer than (\d+) characters$/, 'personFieldLong', ['name', 'field', 'limit']],
  [/^(.+): roles must be a list$/, 'rolesNotList', ['name']],
  [/^(.+): at most (\d+) roles$/, 'rolesTooMany', ['name', 'max']],
  [/^(.+): duplicate roles$/, 'rolesDuplicate', ['name']],
  [/^(.+): not in the setup role list: (.+)$/, 'rolesUnknown', ['name', 'roles']],
  [/^(.+): role must equal the first of roles$/, 'roleFirst', ['name']],

  [/^roster manager required: /, 'managerRequired'],
  [/^you have no roster role -- ask the roster manager$/, 'noRole'],
  [/^leaver$/, 'leaver'],
  [/^no access level$/, 'noAccessLevel'],
  [/^you are on no project in the published roster$/, 'noProjectPublished'],
  [/^you hold roster manager, but only full-time internal staff on the roster can plan it -- ask an administrator$/, 'managerNotFullTime'],
  [/^only a roster manager plans$/, 'onlyManagerPlans'],
  [/^you are on no project yet$/, 'noProjectYet'],

  [/^this day was changed elsewhere while this was being saved; reload and try again$/, 'cellChanged'],
  [/^this company has no roster yet$/, 'noRosterYet'],

  [/^the roster cannot be planned for a day that has already passed -- today is (\S+), refused for (.+)\. An administrator can make this change\.$/, 'pastPlanAdmin', ['today', 'days']],
  [/^the roster cannot be planned for a day that has already passed -- today is (\S+), refused for (.+)\.$/, 'pastPlan', ['today', 'days']],
  [/^a shift cannot be filed for a day that has not happened yet -- today is (\S+), refused for (.+)\.$/, 'futureFile', ['today', 'days']],
  [/^(.+) falls in the (\d{4}-\d{2}) payroll month, which is closed; the roster cannot be planned there\. An administrator can make this change\.$/, 'closedMonth', ['days', 'month']],

  [/^eligible must be a map of project -> ids$/, 'eligibleShape'],
  [/^eligible\[(.+)\] must be a list$/, 'eligibleList', ['project']],
  [/^eligible\[(.+)\]: not an id: (.+)$/, 'eligibleNotId', ['project', 'value']],

  [/^roster staff of (.+) must be a list of person ids$/, 'staffListOf', ['project']],
  [/^roster staff must be person ids: (.+)$/, 'staffIds', ['value']],
  [/^staff must map a project to person ids$/, 'staffMapShape'],
  [/^roster staff must be a person on the roster: (.+)$/, 'staffNotPerson', ['value']],
  [/^roster staff need a home project on the roster: (.+)$/, 'staffNoHome', ['value']],
  [/^(.+) cannot be roster staff of (.+): it is not a project on the roster$/, 'staffNotProject', ['who', 'project']],
  [/^(.+) cannot be roster staff of (.+): only the company's own staff can be roster staff, and they are an external$/, 'staffExternalOwn', ['who', 'project']],
  [/^(.+) cannot be roster staff of (.+): only (Trigteq staff) can be roster staff, and they are an external$/, 'staffExternalNamed', ['who', 'project', 'staff']],
  [/^(.+) cannot be roster staff of (.+): they are not part of it \(their home project, or a published shift on it\)$/, 'staffNotPart', ['who', 'project']],

  [/^the roster has changed since you loaded it \(yours \d+, now \d+\) -- reload and reapply$/, 'versionStale'],
  [/^this roster was loaded as the published, filtered view and cannot be saved -- reload the page and reapply your change$/, 'publishedView'],
  [/^the Plan changes only by Publish or Clean -- reload the page and save your change to the Draft$/, 'planReadOnly'],
  [/^these roster names match nobody on record: (.+) -- add them under an existing person, or have an administrator record the alias first$/, 'namesUnresolved', ['names']],
  [/^planProject: not a roster project: (.+)$/, 'planProjectUnknown', ['projects']],

  [/^code (.+) has type (.+): a T or DT code must be named exactly its type \(T is T, DT is DT\)$/, 'codeExact', ['code', 'act']],
  [/^code (.+) already exists, so its category cannot change$/, 'codeCategory', ['code']],
  [/^code (.+) has no project: codes not tied to a project cannot be added$/, 'codeNoProject', ['code']],
  [/^code (.+) is not tied to a project and cannot be removed or renamed$/, 'codeFixedRemoved', ['code']],
  [/^code (.+) is not tied to a project and cannot be given one$/, 'codeFixedGivenProject', ['code']],
  [/^code (.+) is not tied to a project, so its type cannot change$/, 'codeFixedType', ['code']],

  [/^a day cannot hold two shifts of the same type \((.+?)\): (.+)$/, 'stackSameType', ['type', 'value']],
  [/^a day cannot hold more than two shifts: (.+)$/, 'stackTooMany', ['value']],
  [/^transit hours need T in the code, and the code is (.+)$/, 'hoursTransit', ['code']],
  [/^downtime hours need DT in the code, and the code is (.+)$/, 'hoursDowntime', ['code']],
  [/^a T day holds transit hours only, and this one also has (.+) hours$/, 'hoursTOnly', ['kinds']],

  [/^(\d+) planned cells would be cleared -- confirm to clear them /, 'massClear', ['n']],

  [new RegExp(`^publish_until: ${DAY} is before today \\(${DAY}\\)$`), 'publishBeforeToday', ['day', 'today']],
  [new RegExp(`^clean_from: ${DAY} is before today \\(${DAY}\\); past days stay as they are$`), 'cleanBeforeToday', ['day', 'today']],
  [new RegExp(`^clean_to: ${DAY} is before ${DAY}$`), 'cleanToBefore', ['to', 'from']],
  [/^unknown roster project: (.+)$/, 'unknownProject', ['project']],
  [/^unknown roster name: (.+)$/, 'unknownName', ['name']],
  [/^(.+) is not one of the roster's projects$/, 'homeNotProject', ['home']],
  [/^no such person$/, 'noSuchPerson'],
  [/^(.+) is not an active HR staff member and has no last working day left to file -- only they can be added to the roster from here$/, 'notActiveStaff', ['name']],
  [/^(.+) is already on the roster$/, 'alreadyOnRoster', ['name']],
  [/^a roster name is required$/, 'nameRequired'],
  [/^the roster name "(.+)" is already in use -- pick a different one$/, 'nameInUse', ['name']],
  [/^the roster name "(.+)" is already linked to a different person -- pick a different one, or have an administrator move the link first$/, 'nameLinked', ['name']],
  [/^that person is not on the roster$/, 'notOnRoster'],

  [new RegExp(`^(.+)'s last working day is ${DAY}; an actual cannot be filed for ${DAY}, after it$`), 'afterLastDay', ['name', 'last', 'day']],
  [new RegExp(`^that day is in a closed payroll period; filing reaches back to ${DAY}$`), 'closedPeriod', ['floor']],
  [/^(.+) is not on the roster yet -- add them to the roster first$/, 'notOnRosterYet', ['name']],
  [/^roster person not found$/, 'personNotFound'],
  [/^the person record is missing$/, 'recordMissing'],
  [/^choose the project (.+)'s day is filed under: one of (.+)$/, 'chooseProjectOf', ['name', 'projects']],
  [/^choose the project (.+)'s day is filed under$/, 'chooseProject', ['name']],
  [new RegExp(`^(.+) is already filed for ${DAY} \\((.*)\\)\\. Send the same filing again with confirm_overwrite to replace it, or withdraw the day first\\.$`), 'alreadyFiled', ['name', 'day', 'summary']],
  [/^(.+) has no roster code for this shift, so the day would be filed but never drawn -- file it under a project the roster has codes for$/, 'noRosterCode', ['project']],
  [new RegExp(`^nothing is filed for ${DAY}$`), 'nothingFiled', ['day']],
  [new RegExp(`^${DAY} came from the SharePoint import and cannot be withdrawn here -- a re-import would bring it straight back\\. Correct the source\\.$`), 'sharepoint', ['day']],

  [/^`to` is before `from`$/, 'toBeforeFrom'],
  [/^the window is at most (\d+) days$/, 'windowMax', ['max']],

  [/^`cells` must be a non-empty list$/, 'cellsNonEmpty'],
  [/^at most (\d+) cells per request$/, 'cellsMax', ['max']],
  [/^cell (\d+) is not \{personId, day, was, now\}$/, 'cellShape', ['index']],
  [/^cell (\d+) has no valid personId and day$/, 'cellNoId', ['index']],
  [/^cell (\d+): `was` and `now` are strings$/, 'cellStrings', ['index']],
  [/^cell (\d+) repeats (\S+) (\S+)$/, 'cellRepeats', ['index', 'person', 'day']],
  [/^cell (\d+): `project` is a string$/, 'cellProjectString', ['index']],
  [/^outside the planning horizon: (.+)$/, 'outsideHorizon', ['cells']],
  [/^not on the roster: (.+)$/, 'cellsNotOnRoster', ['cells']],
  [/^not in the roster's codes: (.+)$/, 'unknownCodes', ['codes']],
  [/^a project is set only on a T or DT cell: (.+)$/, 'projectOnlyTransit', ['cells']],
  [/^not a roster project: (.+)$/, 'notRosterProject', ['projects']],
  [/^these cells changed since you loaded them -- reload them and reapply$/, 'cellsChanged'],

  [/^(.+) is retired -- the home must be an active project$/, 'retiredHome', ['projects']],
  [/^(.+) is retired -- (.+)'s home must be an active project$/, 'retiredNamedHome', ['projects', 'name']],
  [/^(.+) is retired -- plan the cell for an active project$/, 'retiredPlan', ['projects']],
  [/^(.+) is retired -- file the day under an active project$/, 'retiredFile', ['projects']],
  [/^(.+) is hidden -- plan the cell for a shown project$/, 'hiddenPlan', ['projects']],
  [/^(.+) is hidden -- file the day under a shown project$/, 'hiddenFile', ['projects']],

  [/^only a roster manager or HR marks city days$/, 'cityOnlyWriters'],
  [/^not on the roster$/, 'cityNotOnRoster'],
  [/^`end` is before `start`$/, 'cityEndBeforeStart'],
  [/^a mark covers at most (\d+) days$/, 'cityMarkTooLong', ['max']],
  [/^no such city mark$/, 'cityNoMark'],

  [/^product not found$/, 'productNotFound'],
  [/^only a Project can be on the roster$/, 'onlyProjectOnRoster'],
  [/^a project on the roster needs a Mongolian name: the roster stores it$/, 'projectNeedsMongolian'],
  [/^only a roster manager sets up projects -- ask a roster manager$/, 'onlyManagerSetsProjects'],
  [/^(.+) already uses the roster name (.+)$/, 'rosterNameUsed', ['project', 'name']],
  [/^(.+) is another product's English name; pick a different roster name$/, 'rosterNameEnglish', ['name']],
  [/^(.+) cannot be renamed while the roster still uses it \((.+)\); clear those first$/, 'projectInUseRenamed', ['project', 'uses']],
  [/^(.+) cannot be taken off the roster while the roster still uses it \((.+)\); clear those first$/, 'projectInUseOff', ['project', 'uses']],

  [/^read-only while viewing as (.+): stop impersonating \(drop the \S+ header\) and repeat the request as yourself$/, 'viewingReadOnly', ['who']],
  [/^account is inactive$/, 'accountInactive'],
  [/^your employment has ended, so this account can now see only your own payslips$/, 'leaverPayslips'],
  [/^this account has no access level, so there is nothing here to show -- you are signed in, but access has not been granted to this account$/, 'noAccessGranted'],
  [/^this is the public view, which shows only the company dashboard and the company calendar -- it is signed in, and this is not a page it may open$/, 'publicViewOnly'],
  [/^this account's access level does not include the everyday staff pages -- it reaches only the pages its level names$/, 'noEverydayPages'],
  [/^missing permission: (.+)$/, 'missingPermission', ['ability']],
  [/^administrator access required$/, 'adminRequired'],
  [/^user not found$/, 'userNotFound'],
  [/^roster_manager_admin: an administrator is already a roster manager$/, 'tickAdmin'],
  [/^roster_manager_not_staff: only full-time internal staff on the roster can be a roster manager$/, 'tickNotStaff'],
  [/^roster_manager_no_account: this person has no ordinary account, so the tick would do nothing$/, 'tickNoAccount'],
  [/^password_holder: roster manager grants more than basic access and signs in with Microsoft only -- remove the password first$/, 'tickPasswordHolder'],
  [/^password_needs_email: this level signs in with a code sent by email, and this person has no email -- give them an email first$/, 'tickNeedsEmail'],

  [/^String should have at most (\d+) characters?$/, 'stringTooLong', ['max']],
  [/^String should have at least (\d+) characters?$/, 'stringTooShort', ['min']],

  [/^a shift value cannot be negative$/, 'shiftNegative'],
  [/^a task count cannot be negative$/, 'taskNegative'],
  [/^a task count must be a whole number$/, 'taskWhole'],
  [/^every shift value and the task count are zero -- use DELETE to clear the day rather than filing an empty one$/, 'allZero'],
  [/^a shift value cannot exceed 1 -- that is a full shift$/, 'shiftOverOne'],
  [/^a day's shift values cannot add up to more than 2 -- a day plus a night, or work plus the transit off site$/, 'shiftSumOverTwo'],
]

const KIND = 'day|night|transit|logging|downtime|tasks'
const FILED_FIGURES = new RegExp(`^(.*?), ((?:${KIND}) [^,]+(?:, (?:${KIND}) [^,]+)*)$`)

function kindWord(kind: string, t: Translate): string {
  return t(`rosterRefusal.kind.${kind}`)
}

/** `_filed_summary`'s `<project>, <kind> <figure>, ...`: the project and figures stay as sent, the words between them translate. */
function filedSummary(summary: string, t: Translate): string {
  const figures = FILED_FIGURES.exec(summary)
  const project = figures ? figures[1]! : summary.replace(/, nothing recorded$/, '')
  const shownProject = project === 'no project' ? t('rosterRefusal.noProject') : project
  const shownFigures = figures
    ? figures[2]!.split(', ').map((part) => {
        const [kind, value] = part.split(' ')
        return `${kindWord(kind!, t)} ${value}`
      }).join(', ')
    : t('rosterRefusal.nothingRecorded')
  return `${shownProject}, ${shownFigures}`
}

function paramValue(name: string, value: string, t: Translate): string {
  if (name === 'summary') return filedSummary(value, t)
  if (name === 'kinds') return value.split(', ').map((kind) => kindWord(kind, t)).join(', ')
  return value === '(blank)' ? t('rosterRefusal.blank') : value
}

function fromRules(body: string, t: Translate): string | null {
  for (const [pattern, key, params = []] of RULES) {
    const match = pattern.exec(body)
    if (!match) continue
    const values = Object.fromEntries(params.map((name, i) => [name, paramValue(name, match[i + 1]!, t)]))
    return t(`rosterRefusal.${key}`, values)
  }
  return null
}

/** The refusal in the reader's language, or the server's sentence unchanged when its wording is not known here. */
export function localizeRosterRefusal(detail: string, t: Translate): string {
  const body = detail.replace(WIRE_PREFIX, '')
  const whole = fromRules(body, t)
  if (whole !== null) return whole
  const onDay = ON_DAY.exec(body)
  const inner = onDay ? fromRules(onDay[3]!, t) : null
  return onDay && inner !== null ? `${onDay[1]} ${onDay[2]}: ${inner}` : detail
}

export function rosterErrorMessage(error: unknown, t: Translate): string {
  return localizeRosterRefusal(errorMessage(error), t)
}
